import React, { useEffect, useState } from 'react'
import styles from './BillingInfo.module.css'
import PaymentMethod from '../PaymentMethod/PaymentMethod'
import { getPaymentMethods, updatePaymentMethodBilling } from '../../Store/apiStore'

// Billing Info tab.
//
// Payment Methods is the SAME component the Edit Profile page uses
// (../PaymentMethod/PaymentMethod) — identical cards, the identical Stripe
// "Add new" popup and the identical APIs. Reused, not re-implemented.
//
// The "Billing information" block shows the DEFAULT card's billing details
// (name / email / address) — the card that actually bills the running
// subscription. Data comes from getPaymentMethods (Stripe paymentMethods.list),
// each card carrying billing_details. Clicking Edit turns the read-only rows
// into a form; Save writes back to that card's billing_details on Stripe
// (updatePaymentMethodBilling → paymentMethods.update). Only the card is
// touched — never the customer.

// "969 Cox Rd, Gastonia, NC 28054" style — join the parts Stripe gives us,
// skipping any that are empty.
const formatAddress = (addr) => {
  if (!addr) return ''
  const line1 = [addr.line1, addr.line2].filter(Boolean).join(', ')
  const cityState = [addr.city, addr.state].filter(Boolean).join(', ')
  const tail = [cityState, addr.postalCode].filter(Boolean).join(' ')
  return [line1, tail, addr.country].filter(Boolean).join(', ')
}

// Blank editable form shape.
const emptyForm = {
  name: '', email: '',
  line1: '', line2: '', city: '', state: '', postalCode: '', country: '',
}

const BillingInfo = () => {
  const [card, setCard] = useState(null)   // the default card (with billing_details)
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState(emptyForm)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [refresh, setRefresh] = useState(0)

  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        const data = await getPaymentMethods()
        if (!alive) return
        const cards = data?.paymentMethods || []
        const defaultId = data?.defaultPaymentMethodId
        const defaultCard =
          cards.find((c) => c.id === defaultId) ||
          cards.find((c) => c.isDefault) ||
          // Fallback: no explicit default set on the customer (e.g. some PAYG
          // flows don't set invoice_settings.default_payment_method). A single
          // card on file IS effectively the billing card, so show it rather
          // than leaving email/name/address blank.
          cards[0] ||
          null
        setCard(defaultCard)
      } catch (e) {
        console.error('[BillingInfo] load failed:', e?.message)
      }
    })()
    return () => { alive = false }
  }, [refresh])

  const email = card?.email || ''
  const name = card?.name || ''
  const address = formatAddress(card?.address) || ''

  // Enter edit mode — seed the form from the card's current values.
  const startEdit = () => {
    if (!card) return
    const a = card.address || {}
    setForm({
      name: card.name || '',
      email: card.email || '',
      line1: a.line1 || '',
      line2: a.line2 || '',
      city: a.city || '',
      state: a.state || '',
      postalCode: a.postalCode || '',
      country: a.country || '',
    })
    setError('')
    setEditing(true)
  }

  const cancelEdit = () => {
    setEditing(false)
    setError('')
  }

  const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }))

  const handleSave = async () => {
    if (!card?.id || saving) return

    // Normalise before validating/sending: trim every field, lowercase the
    // email, uppercase the 2-letter country code. All fields are optional (same
    // as before) — we only validate the two things Stripe actually enforces.
    const name = String(form.name || '').trim()
    const email = String(form.email || '').trim().toLowerCase()
    const line1 = String(form.line1 || '').trim()
    const line2 = String(form.line2 || '').trim()
    const city = String(form.city || '').trim()
    const state = String(form.state || '').trim()
    const postalCode = String(form.postalCode || '').trim()
    const country = String(form.country || '').trim().toUpperCase()

    // Email — only format-checked when filled (field is optional). Stripe
    // rejects a malformed email, so catch it here with a friendly message.
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address.')
      return
    }
    // Country — Stripe requires a 2-letter ISO code (e.g. US, IN). Only checked
    // when filled.
    if (country && !/^[A-Z]{2}$/.test(country)) {
      setError('Country must be a 2-letter code (e.g. US, IN).')
      return
    }

    setSaving(true)
    setError('')
    try {
      await updatePaymentMethodBilling(card.id, {
        name,
        email,
        address: { line1, line2, city, state, postalCode, country },
      })
      setEditing(false)
      setRefresh((p) => p + 1) // re-fetch so the read-only view shows new values
    } catch (err) {
      setError(err?.message || 'Could not save. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <section className={styles.panel} aria-label="Billing information">
      <div className={styles.headingRow}>
        <h3>Billing information</h3>
        {/* Edit only when there's a card and we're not already editing. */}
        {!editing && card && (
          <button type="button" className={styles.outlineButton} onClick={startEdit}>Edit</button>
        )}
      </div>

      {editing ? (
        // ---- Editable form (Name / Email / full Address) with Cancel + Save ----
        <div className={styles.editForm}>
          {/* maxLength caps are UX limits to keep input sane (Stripe doesn't
              publish per-field maxes). Only email format and the 2-letter
              country code are Stripe-enforced — checked on Save. */}
          <label className={styles.field}>
            <span>Name</span>
            <input type="text" value={form.name} onChange={setField('name')} placeholder="Cardholder name" maxLength={200} />
          </label>
          <label className={styles.field}>
            <span>Billing email</span>
            <input type="email" value={form.email} onChange={setField('email')} placeholder="name@example.com" maxLength={254} />
          </label>
          <label className={styles.field}>
            <span>Street address</span>
            <input type="text" value={form.line1} onChange={setField('line1')} placeholder="Street address" maxLength={200} />
          </label>
          <label className={styles.field}>
            <span>Apt / Suite (optional)</span>
            <input type="text" value={form.line2} onChange={setField('line2')} placeholder="Apartment, suite, etc." maxLength={200} />
          </label>
          <div className={styles.fieldRow}>
            <label className={styles.field}>
              <span>City</span>
              <input type="text" value={form.city} onChange={setField('city')} placeholder="City" maxLength={100} />
            </label>
            <label className={styles.field}>
              <span>State</span>
              <input type="text" value={form.state} onChange={setField('state')} placeholder="State" maxLength={100} />
            </label>
          </div>
          <div className={styles.fieldRow}>
            <label className={styles.field}>
              <span>ZIP / Postal</span>
              <input type="text" value={form.postalCode} onChange={setField('postalCode')} placeholder="Postal code" maxLength={50} />
            </label>
            <label className={styles.field}>
              <span>Country</span>
              {/* 2-letter ISO code, auto-uppercased as the user types so "in" → "IN". */}
              <input
                type="text"
                value={form.country}
                onChange={(e) => setForm((f) => ({ ...f, country: e.target.value.toUpperCase() }))}
                placeholder="e.g. US, IN"
                maxLength={2}
              />
            </label>
          </div>

          {error && <p className={styles.editError}>{error}</p>}

          <div className={styles.editActions}>
            <button type="button" className={styles.cancelBtn} onClick={cancelEdit} disabled={saving}>Cancel</button>
            <button type="button" className={styles.saveBtn} onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      ) : (
        // ---- Read-only view ----
        <dl className={styles.details}>
          <div>
            <dt>Billing email</dt>
            <dd>{email || '—'}</dd>
          </div>
          <div>
            <dt>Name</dt>
            <dd>{name || '—'}</dd>
          </div>
          <div>
            <dt>Address</dt>
            <dd>{address || '—'}</dd>
          </div>
        </dl>
      )}

      <div className={styles.separator} />

      {/* Payment Methods — Figma-style list (Add new, Default badge, ⋮ menu with
          Set-default / Remove). onDefaultChange re-fetches so the Billing
          information block above shows the NEW default card's details. */}
      <PaymentMethod onDefaultChange={() => setRefresh((p) => p + 1)} />
    </section>
  )
}

export default BillingInfo
